import React from 'react'

import {
  Box,
  CellRenderer,
  CellRenderers,
  CheckboxGroup,
  CheckboxGroupProps,
  Column,
  formatMoney,
  FormControlLabel,
  MenuOption,
  ProviderLogo,
  Radio,
  SimpleTableProps,
} from '@percept/mui'

import { DataWarehouseProvider, DataWarehouseQueryResponse } from './hooks'

import { Dictionary, PlatformUnit, PlatformUnitContainer, TimeseriesGranularity } from '@percept/types'

import { deslugify, formatUTC, separateThousands } from '@percept/utils'

import { intersection, xor } from 'lodash-es'

import {
  automaticPrecisionPercentageFormatter,
  dmyFormatter,
  financialYearFormatter,
  longMonthYearFormatter,
  quarterFormatter,
  weekFormatter,
} from '@percept/mui/charts'

import { performanceReportingDimensionOrder, providerLabelMap } from '@percept/constants'


export type GenericTableRow = Dictionary

export const isNewLayout = process.env.APP !== 'vodafone'

const timeseriesDimensionLabels: Record<string, string> = {
  period_start: 'Period',
}

const timeseriesDimensions: string[] = [
  'period_start',
]

const primaryDimensionOrder: string[] = [
  'org_unit_name',
  'organisation',
  'provider',
  ...timeseriesDimensions,
]

export const monetaryColumns: string[] = [
  'cost',
  'cost_per_conversion',
  'average_cpc',
  'average_cpm',
  'average_cpv',
]
export const numericColumns: string[] = [
  'impressions',
  'conversions',
  'video_views',
  'clicks',
]
export const rateBasedColumns: string[] = [
  'average_ctr',
]

/* eslint-disable react/display-name, react/prop-types */
const providerLogoRenderer: CellRenderer<GenericTableRow> = ({ provider }) => (
  <ProviderLogo
    provider={provider as DataWarehouseProvider}
    position='relative'
    top={3}
    size={1.35} />
)
const providerLabelRenderer: CellRenderer<GenericTableRow> = ({ provider }) => (
  <>{providerLabelMap[provider as DataWarehouseProvider]}</>
)
/* eslint-enable react/display-name, react/prop-types */

const dateRenderersByGranularity: Record<TimeseriesGranularity, (value: string | Date) => string> = {
  FINANCIAL_YEAR: financialYearFormatter,
  CALENDAR_YEAR: d => formatUTC(d, 'yyyy'),
  DAY: dmyFormatter,
  WEEK: weekFormatter,
  MONTH: longMonthYearFormatter,
  QUARTER: quarterFormatter,
}


const defaultCellRenderers: CellRenderers<GenericTableRow> = {
  provider: isNewLayout ? providerLabelRenderer : providerLogoRenderer,
}

const displayNA = <span style={{opacity: 0.5}}>N / A</span>

const nullFormatter = (value: string | number | null, formatter: (value: number) => string) => {
  if( value === null ){
    return displayNA
  }
  return formatter(Number(value))
}

const makeCellRenderers = (currency: string): CellRenderers<GenericTableRow> => {
  return {
    ...numericColumns.reduce( (acc, column) => {
      acc[column] = (row) => (
        <>{nullFormatter(row[column], separateThousands)}</>
      )
      return acc
    }, {} as Record<string, CellRenderer<GenericTableRow>>),
    ...rateBasedColumns.reduce( (acc, column) => {
      acc[column] = (row) => (
        <>{nullFormatter(row[column], val => automaticPrecisionPercentageFormatter(val * 100))}</>
      )
      return acc
    }, {} as Record<string, CellRenderer<GenericTableRow>>),
    ...monetaryColumns.reduce( (acc, column) => {
      acc[column] = (row) => (
        <>{nullFormatter(row[column], amount => formatMoney({ amount, abbreviate: false, currency }))}</>
      )
      return acc
    }, {} as Record<string, CellRenderer<GenericTableRow>>)
  }
}

const getDimensionSortValue = (value: string): string => (
  value.toUpperCase()
)


const getOrderedKeys = (keys: string[], order: string[]): string[] => {
  const recognisedKeys = intersection(order, keys)
  const remainingKeys = xor(recognisedKeys, keys)
  remainingKeys.sort()
  return [
    ...recognisedKeys,
    ...remainingKeys,
  ]
}

export const getMetricLabel = (key: string): string => {
  key = key.replace('average', 'Avg.')
  let deslugified = deslugify(key)
  for( const m of ['Cpc', 'Ctr', 'Cpm', 'Cpv', 'Cpa'] ){
    deslugified = deslugified.replace(m, m.toUpperCase())
  }
  return deslugified
}


export const getDataWarehouseTableProps = (
  queryResponse: DataWarehouseQueryResponse,
  currency: string,
  granularity?: TimeseriesGranularity,
  totalsResponse?: DataWarehouseQueryResponse,
): Pick<SimpleTableProps<GenericTableRow>, 'columns' | 'rows' | 'footerRows' | 'cellFontWeight' | 'renderers' | 'defaultRenderer'> => {
  
  const dimensions = queryResponse.metadata.dimensions
  const sampleRow = queryResponse.rows[0]
  const metricKeys = (
    sampleRow ?
      Object.keys(sampleRow.metrics) : [
        ...monetaryColumns,
        ...numericColumns,
        ...rateBasedColumns,
      ]
  )

  const isTimeseries = !!(sampleRow && sampleRow.dimensions.period_start)
  const dimensionKeys = Object.keys(dimensions)
  const timeseriesCellRenderers: CellRenderers<GenericTableRow> = {}
  if( isTimeseries && granularity ){
    dimensionKeys.push('period_start')
    const formatter = dateRenderersByGranularity[granularity]
    timeseriesCellRenderers.period_start = ({ period_start }) => <>{period_start ? formatter(period_start) : displayNA}</>
  }

  const orderedMetricKeys = getOrderedKeys(metricKeys, performanceReportingDimensionOrder)
  const orderedDimensionKeys = getOrderedKeys(dimensionKeys, primaryDimensionOrder)

  const visibleDimensions = orderedDimensionKeys.filter( k => timeseriesDimensions.includes(k) || dimensions[k].internal !== true)

  const columns: Column<GenericTableRow>[] = [
    ...visibleDimensions.map( key => ({
      key,
      label: timeseriesDimensionLabels[key] || dimensions[key].label,
      align: 'left',
      getSortValue: getDimensionSortValue,
    }) as Column<GenericTableRow>),
    ...orderedMetricKeys.map( key => ({
      key,
      label: getMetricLabel(key),
      align: 'right'
    }) as Column<GenericTableRow>)
  ]

  const rows: GenericTableRow[] = queryResponse.rows.map( row => ({
    ...row.dimensions,
    ...row.metrics,
  }))

  const footerRows: GenericTableRow[] | undefined = totalsResponse && totalsResponse.rows.map( row => ({
    ...row.dimensions,
    ...row.metrics,
  }))

  return {
    columns,
    rows,
    footerRows,
    
    cellFontWeight: isNewLayout ? 'regular' : undefined,
    renderers: {
      ...defaultCellRenderers,
      ...makeCellRenderers(currency),
      ...timeseriesCellRenderers,
    },
  }
}


export const DataWarehouseOrgUnitSelect = ({
  orgUnit,
  selectedIds,
  enabledOrgs,
  onChange,
  CheckboxGroupProps = {},
}: {
  orgUnit: PlatformUnitContainer
  selectedIds: string[]
  enabledOrgs: string[]
  onChange: (selectedIds: string[]) => void
  CheckboxGroupProps?: Partial<CheckboxGroupProps<string>>
}): JSX.Element => {
  const isRootSelected = selectedIds.includes(orgUnit.id)
  const unitType = deslugify(orgUnit.unit_type || 'ORGANISATION')
  return (
    <Box display='flex' flexDirection='column'>
      <FormControlLabel
        label={orgUnit.name}
        checked={isRootSelected}
        control={<Radio />}
        onChange={(): void => {
          onChange([orgUnit.id])
        }} />
      <FormControlLabel
        label={`Segment by ${unitType}`}
        checked={!isRootSelected}
        control={<Radio />}
        onChange={(): void => {
          onChange([])
        }} />
      <CheckboxGroup
        name={null}
        disabled={isRootSelected}
        {...CheckboxGroupProps}
        value={selectedIds}
        options={
          orgUnit.children.map( org => ({
            value: org.id,
            label: org.name,
            disabled: !enabledOrgs.includes(org.id),
          })) as MenuOption<string>[]
        }
        onChange={onChange} />
    </Box>
  )
}
